import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonCard,
  IonContent,
  IonIcon,
  IonLabel,
  IonFab,
  IonFabButton,
  IonImg,
  IonFabList,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonPage,
  useIonAlert,
  IonCardContent,
  IonCardHeader,
  useIonModal,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
} from "@ionic/react";
import React, { useState } from "react";
import { RefresherEventDetail } from "@ionic/core";
import {
  chevronDownCircleOutline,
  arrowBackOutline,
  menuSharp,
  calendar,
  createOutline,
  personCircleOutline,
  callOutline,
  chatbubbleOutline,
  trashBinOutline,
  documentTextOutline,
  informationCircleOutline,
  helpCircleOutline,
  calendarNumberOutline,
  enterOutline,
  mapOutline,
  pushOutline,
} from "ionicons/icons";
import { RestApi } from "../../services/Api";
import { CustomerFollowUp } from "../../models/Modal";
import { useTranslation } from "react-i18next";
import {
  getNameDate,
  getRoleMenu,
  useConstructor,
} from "../../services/Library";
import { useSelector } from "react-redux";
import Form from "./Form";
import HistoryVisit from '../visit/History';
import HistoryCustomerFollowUp from './History';
import "./Detail.scss";

const Detail: React.FC<CustomerFollowUp> = (props) => {
  const [customerFollowUp, setCustomerFollowUp] = useState<any>({});
  const [alert] = useIonAlert();
  const { t } = useTranslation(["general"]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const user = useSelector((state: any) => state.user.value);
  const getRole = getRoleMenu(user.role_menu, "customer_follow_up");
  const [activeTab, setActiveTab] = useState<string>("main");

  const statusOptions: any = {
    interested: {
      name: t("status_interested"),
      color: "#48bb78",
      bgColor: "#f0fff4",
      icon: informationCircleOutline,
      label: "Interested",
    },
    negotiation: {
      name: t("status_negotiation"),
      color: "#ecc94b",
      bgColor: "#fefcbf",
      icon: calendar,
      label: "Negotiation",
    },
    waiting: {
      name: t("status_waiting"),
      color: "#a0aec0",
      bgColor: "#f7fafc",
      icon: helpCircleOutline,
      label: "Waiting",
    }
  };

  const contactMethodOptions: any = {
    call: { icon: callOutline, label: t("call"  ), color: "#4299e1" },
    massage: { icon: chatbubbleOutline, label: t("massage"), color: "#48bb78" },
  };

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
    });
  };

  const handleDismissHistoryVisit = (e: any) => {
    dismissHistoryVisit();
  };

  const handleDismissHistoryCustomerFollowUp = (e: any) => {
    dismissHistoryCustomerFollowUp();
  };
  
  const [presentHistoryVisit, dismissHistoryVisit] = useIonModal(HistoryVisit, {
    idCustomer: customerFollowUp?.customer?.customer_id,
    onDismissDetail: handleDismissHistoryVisit,
  });

  const [presentHistoryCustomerFollowUp, dismissHistoryCustomerFollowUp] =
    useIonModal(HistoryCustomerFollowUp, {
      idCustomer: customerFollowUp?.customer?.customer_id,
      onDismissDetail: handleDismissHistoryCustomerFollowUp,
    });

  const handleDismissForm = () => {
    dismissForm();
    getCustomerFollowUpDetail(props.idCustomerFollowUp);
  };

  const [presentForm, dismissForm] = useIonModal(Form, {
    idCustomerFollowUp: props.idCustomerFollowUp,
    type: "edit",
    onDismiss: handleDismissForm,
  });

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getCustomerFollowUpDetail(props.idCustomerFollowUp);
    event.detail.complete();
  };

  const getCustomerFollowUpDetail = (id: number) => {
    if (!id) {
      props.onDismiss();
      return;
    }
    setLoaded(false);
    RestApi(`customer-follow-up/${id}`, "get", {}, "application/json")
      .then(async (res: any) => {
        if (res.data.code === 200) {
          setCustomerFollowUp(res.data.data);
          setLoaded(true);
        } else {
          props.onDismiss();
          massageAlert("error", res.data.message);
        }
      })
      .catch((err: any) => {
        props.onDismiss();
        massageAlert("error", err.message);
      });
  };

  const handleDelete = async () => {
    alert({
      header: "Confirm Delete",
      message: "Are you sure you want to delete this follow-up record?",
      cssClass: "header-alert",
      buttons: [
        {
          text: "Cancel",
          cssClass: "alert-button-cancel",
          role: "cancel",
        },
        {
          text: "Delete",
          cssClass: "alert-button-confirm",
          role: "confirm",
          handler: async () => {
            try {
              await RestApi(
                `customer-follow-up/${props.idCustomerFollowUp}/delete`,
                "get",
                {},
                "application/json",
              )
                .then((res: any) => {
                  if (res.data.code === 200) {
                    massageAlert("success", "Data deleted successfully");
                    props.onDismiss();
                  } else {
                    massageAlert("error", res.data.message);
                  }
                })
                .catch((err: any) => {
                  massageAlert("error", err.message);
                });
            } catch (error: any) {
              massageAlert("error", error.message);
            }
          },
        },
      ],
    });
  };

  useConstructor(() => {
    getCustomerFollowUpDetail(props.idCustomerFollowUp);
  });

  const [segment, setSegment] = useState<string>("information");
  const [segmentImage, setSegmentImage] = useState<string>(
    "customer_identity_card_image",
  );
  const renderCustomerDetails = (customer: any) => {
    if (!customer) return null;

    return (
      <>
        <IonCard className="ion-no-padding ion-no-margin">
          <IonSegment
            scrollable={true}
            value={segment}
            className="ion-no-padding ion-no-margin"
          >
            <IonSegmentButton
              value="information"
              onClick={() => {
                setSegment("information");
              }}
            >
              <IonLabel>{t("information")}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton
              value="address"
              onClick={() => {
                setSegment("address");
              }}
            >
              <IonLabel>{t("address")}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton
              value="contact"
              onClick={() => {
                setSegment("contact");
              }}
            >
              <IonLabel>{t("contact")}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton
              value="detail"
              onClick={() => {
                setSegment("detail");
              }}
            >
              <IonLabel>{t("detail")}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
          <IonCardContent className="ion-no-padding ion-no-margin">
            {segment === "information" && (
              <IonText className="ion-padding-start">
                <p className="ion-no-padding ion-no-margin">
                  Handphone :<strong>{customer.customer_phone}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Telpon :<strong>{customer.customer_phone}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Fax :<strong>{customer.customer_fax}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Email :<strong> {customer.customer_email}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Status :
                  <strong>
                    {customer.customer_is_active ? "AKTIF" : "TIDAK AKTIF"}
                  </strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />

                <p className="ion-no-padding ion-no-margin">
                  Area :
                  <strong>
                    {customer.subarea_name} -{customer.area_name}
                  </strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Kota :<strong> {customer.customer_city}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Propinsi :<strong> {customer.customer_state}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Negara :<strong>{customer.customer_country}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Kode Pos :<strong>{customer.customer_zipcode}</strong>
                </p>
              </IonText>
            )}
            {segment === "contact" && (
              <IonText className="ion-padding-start">
                <p className="ion-no-padding ion-no-margin">
                  Nama Kontak :<strong>{customer.customer_contact_name}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Tanggal Lahir :
                  <strong>
                    {getNameDate(customer.customer_contact_birthday)}
                  </strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Jabatan :<strong>{customer.customer_contact_title}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Hobby :<strong>{customer.customer_contact_hobby}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Handphone :<strong>{customer.customer_contact_mobile}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Email :<strong>{customer.customer_contact_email}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Nama KTP :
                  <strong>{customer.customer_identity_card_name}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  No. KTP :
                  <strong>{customer.customer_identity_card_number}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Nama NPWP :<strong>{customer.customer_tax_name}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
                <p className="ion-no-padding ion-no-margin">
                  Nomor NPWP :<strong>{customer.customer_tax_number}</strong>
                </p>
                <hr className="ion-no-padding ion-no-margin" />
              </IonText>
            )}
            {segment === "address" && (
              <>
                {customer.customer_address.map((address: any) => (
                  <IonCard
                    key={address.customer_address_id}
                    className="ion-margin"
                    style={{
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <IonCardContent>
                      <IonGrid className="ion-no-padding ion-no-margin">
                        <IonRow className="ion-no-padding ion-no-margin ion-align-items-center">
                          <IonCol
                            size="6"
                            className="ion-no-padding ion-no-margin"
                          >
                            <IonChip
                              color={
                                address.customer_address_is_active === 1
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {address.customer_address_is_active === 1
                                ? "Aktif"
                                : "InAktif"}
                            </IonChip>
                          </IonCol>
                          <IonCol
                            size="6"
                            className="ion-no-padding ion-no-margin ion-text-end"
                          >
                            {address.customer_address_is_default === 1 && (
                              <IonIcon
                                icon={informationCircleOutline}
                                color="warning"
                                size="small"
                              />
                            )}
                          </IonCol>
                          <IonCol
                            size="12"
                            className="ion-no-padding ion-no-margin"
                          >
                            <IonText>
                              <h3
                                style={{
                                  margin: 0,
                                  color: "#333",
                                  fontWeight: "bold",
                                }}
                              >
                                {address.customer_address_name}
                              </h3>
                              <p style={{ color: "#666" }}>
                                {address.customer_address}
                              </p>
                              <hr className="ion-no-padding ion-no-margin" />
                            </IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                ))}
              </>
            )}
            {segment === "detail" && (
              <>
                <IonText className="ion-padding-start">
                  <p className="ion-no-padding ion-no-margin">
                    Website URL :<strong> {customer.customer_url}</strong>
                  </p>
                  <hr className="ion-no-padding ion-no-margin" />
                  <p className="ion-no-padding ion-no-margin">
                    Keterangan :<strong>{customer.customer_remark}</strong>
                  </p>
                  <hr className="ion-no-padding ion-no-margin" />
                </IonText>
              </>
            )}
          </IonCardContent>
        </IonCard>
        <IonCard className="ion-no-padding ion-no-margin ion-margin-top">
          <IonTitle className="ion-padding-start ion-no-margin" slot="start">
            <strong>Foto Pelanggan</strong>
          </IonTitle>
          <IonCardContent className="ion-no-padding ion-no-margin">
            <IonSegment
              scrollable={true}
              value={segmentImage}
              className="ion-no-padding ion-no-margin"
            >
              <IonSegmentButton
                value="customer_identity_card_image"
                onClick={() => {
                  setSegmentImage("customer_identity_card_image");
                }}
              >
                <IonLabel>KTP</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton
                value="customer_image1"
                onClick={() => {
                  setSegmentImage("customer_image1");
                }}
              >
                <IonLabel>Foto 1</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton
                value="customer_image2"
                onClick={() => {
                  setSegmentImage("customer_image2");
                }}
              >
                <IonLabel>Foto 2</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton
                value="customer_image3"
                onClick={() => {
                  setSegmentImage("customer_image3");
                }}
              >
                <IonLabel>Foto 3</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton
                value="customer_image4"
                onClick={() => {
                  setSegmentImage("customer_image4");
                }}
              >
                <IonLabel>Foto 4</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            <IonImg
              src={
                customer[segmentImage] ?? `assets/img/image_not_available.png`
              }
            />
          </IonCardContent>
        </IonCard>
      </>
    );
  };

  const renderImageSection = () => {
    if (customerFollowUp.images && Array.isArray(customerFollowUp.images) && customerFollowUp.images.length > 0) {
      return (
        <div className="image-section">
          {customerFollowUp.images.map((img: any, idx: number) => {
            let imageUrl = img.image;
            let description = img.description;

            return (
              <div key={idx} style={{ marginBottom: 12 }}>
                <IonImg
                  src={imageUrl}
                  className="detail-image"
                  style={{ borderRadius: 8, maxHeight: 180, objectFit: "contain" }}
                />
                  <p style={{ marginTop: 4, fontSize: 13, color: "#666", fontStyle: "italic" }}>
                    {description}
                  </p>
              </div>
            );
          })}
        </div>
      );
    }
    if (customerFollowUp.image) {
      return (
        <div className="image-section">
          <IonImg
            src={customerFollowUp.image}
            className="detail-image"
            style={{ marginBottom: 12, borderRadius: 8, maxHeight: 180, objectFit: "contain" }}
          />
        </div>
      );
    }
    return <p className="ion-padding">No images available.</p>;
  };

  const segments: {
    value: string;
    label: string;
    condition?: boolean;
  }[] = [
    {
      value: "main",
      label: t("details")
    },
    {
      value: "customer",
      label: t("module.customer"),
      condition: !!customerFollowUp.customer
    }
  ];

  return (
    <IonPage className="detail-page">
      <IonHeader className="ion-no-border">
        {loaded ? (
          <IonToolbar className="detail-toolbar">
            <IonButton
              fill="clear"
              slot="start"
              onClick={() => props.onDismiss()}
              className="back-button"
            >
              <IonIcon icon={arrowBackOutline} />
            </IonButton>

            <div className="toolbar-content" slot="start">
              <div className="customer-mini-info">
                <h2>{customerFollowUp.customer?.customer_name || "N/A"}</h2>
                <p>ID: {customerFollowUp.customer_id}</p>
              </div>
            </div>

            <div className="toolbar-status" slot="end">
              <div
                className="status-chip"
                style={{
                  backgroundColor:
                    statusOptions[customerFollowUp.status]?.bgColor ||
                    "#f7fafc",
                  color:
                    statusOptions[customerFollowUp.status]?.color || "#4a5568",
                }}
              >
                <IonIcon
                  icon={
                    statusOptions[customerFollowUp.status]?.icon ||
                    helpCircleOutline
                  }
                />
                <span>
                  {statusOptions[customerFollowUp.status]?.label ||
                    customerFollowUp.status}
                </span>
              </div>
            </div>
          </IonToolbar>
        ) : (
          <IonToolbar className="skeleton-toolbar">
            <IonButton fill="clear" slot="start" disabled>
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
            <div className="toolbar-skeleton">
              <IonSkeletonText
                animated
                style={{ width: "40px", height: "40px", borderRadius: "12px" }}
              />
              <div style={{ marginLeft: "12px" }}>
                <IonSkeletonText
                  animated
                  style={{ width: "120px", height: "20px" }}
                />
                <IonSkeletonText
                  animated
                  style={{ width: "80px", height: "16px", marginTop: "4px" }}
                />
              </div>
            </div>
          </IonToolbar>
        )}
      </IonHeader>

      <IonContent className="detail-content ion-no-margin ion-no-padding">
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t("loading")}
            refreshingSpinner="circles"
            refreshingText={t("loading")}
          />
        </IonRefresher>

        {!loaded ? (
          <div className="skeleton-container ">
            <IonCard className="skeleton-card">
              <IonCardHeader>
                <IonSkeletonText
                  animated
                  style={{ width: "150px", height: "24px" }}
                />
              </IonCardHeader>
              <IonCardContent>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="skeleton-item">
                    <IonSkeletonText
                      animated
                      style={{ width: "100px", height: "16px" }}
                    />
                    <IonSkeletonText
                      animated
                      style={{
                        width: "200px",
                        height: "20px",
                        marginTop: "8px",
                      }}
                    />
                  </div>
                ))}
              </IonCardContent>
            </IonCard>
          </div>
        ) : (
          <>
            <IonCard className="detail-card ion-no-margin ion-no-padding">
              <IonCardHeader className="ion-no-margin ion-no-padding">
                <IonSegment
                  value={activeTab}
                  onIonChange={(e) => setActiveTab(e.detail.value as string)}
                >
                  {segments
                    .filter(
                      (seg) => seg.condition === undefined || seg.condition,
                    )
                    .map((seg) => (
                      <IonSegmentButton
                        key={seg.value}
                        value={seg.value}
                        className="ion-no-margin ion-no-padding"
                      >
                        <IonLabel className="ion-no-margin ion-no-padding">
                          {seg.label}
                        </IonLabel>
                      </IonSegmentButton>
                    ))}
                </IonSegment>
              </IonCardHeader>

              <IonCardContent>
                {activeTab === "main" && (
                  <IonGrid className="details-grid">
                    <IonRow>
                      <IonCol size="12">
                        <div className="quick-info-chips ">
                          <IonChip
                            className="info-chip"
                            style={{
                              backgroundColor:
                                contactMethodOptions[
                                  customerFollowUp.contact_method
                                ]?.color + "15" || "#f7fafc",
                              color:
                                contactMethodOptions[
                                  customerFollowUp.contact_method
                                ]?.color || "#4a5568",
                            }}
                          >
                            <IonIcon
                              icon={
                                contactMethodOptions[
                                  customerFollowUp.contact_method
                                ]?.icon || callOutline
                              }
                            />
                            <IonLabel>
                              {contactMethodOptions[
                                customerFollowUp.contact_method
                              ]?.label || customerFollowUp.contact_method}
                            </IonLabel>
                          </IonChip>
                        </div>
                      </IonCol>

                      <IonCol size="6">
                        <div className="detail-item">
                          <IonIcon icon={calendar} className="detail-icon" />
                          <div className="detail-content">
                            <span className="detail-label">Follow Up Date</span>
                            <span className="detail-value">
                              {getNameDate(customerFollowUp.follow_up_date)}
                            </span>
                          </div>
                        </div>
                      </IonCol>

                      <IonCol size="6">
                        <div className="detail-item">
                          <IonIcon
                            icon={calendarNumberOutline}
                            className="detail-icon"
                          />
                          <div className="detail-content">
                            <span className="detail-label">Next Follow Up</span>
                            <span className="detail-value">
                              {customerFollowUp.next_follow_up
                                ? getNameDate(customerFollowUp.next_follow_up)
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </IonCol>

                      <IonCol size="12">
                        <div className="detail-item">
                          <IonIcon
                            icon={documentTextOutline}
                            className="detail-icon"
                          />
                          <div className="detail-content">
                            <p className="detail-label">{t("note")}</p>
                            <p className="detail-value">
                              {customerFollowUp.notes ?? "-"}
                            </p>
                          </div>
                        </div>
                      </IonCol>

                      <IonCol size="12">{renderImageSection()}</IonCol>

                      <IonCol size="6">
                        <div className="detail-item">
                          <IonIcon
                            icon={personCircleOutline}
                            className="detail-icon"
                          />
                          <div className="detail-content">
                            <span className="detail-label">
                              {t("createdBy")}
                            </span>
                            <span className="detail-value">
                              {customerFollowUp.created_by} • <br />
                              {getNameDate(customerFollowUp.created_at, true)}
                            </span>
                          </div>
                        </div>
                      </IonCol>

                      {customerFollowUp.updated_by && (
                        <IonCol size="6">
                          <div className="detail-item">
                            <IonIcon
                              icon={personCircleOutline}
                              className="detail-icon"
                            />
                            <div className="detail-content">
                              <span className="detail-label">
                                {t("updatedBy")}
                              </span>
                              <span className="detail-value">
                                {customerFollowUp.updated_by} • <br />
                                {getNameDate(customerFollowUp.updated_at, true)}
                              </span>
                            </div>
                          </div>
                        </IonCol>
                      )}

                      {customerFollowUp.visit && (
                        <IonCol size="12">
                          <div className="detail-item">
                            <IonIcon
                              icon={enterOutline}
                              className="detail-icon"
                            />
                            <div className="detail-content">
                              <span className="detail-label">
                                Related {t("visit")}
                              </span>
                              <span className="detail-value">
                                #{customerFollowUp.visit_id}
                              </span>
                            </div>
                          </div>
                        </IonCol>
                      )}
                    </IonRow>
                  </IonGrid>
                )}

                {activeTab === "customer" && customerFollowUp.customer && (
                  <div className="">
                    {renderCustomerDetails(customerFollowUp.customer)}
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </>
        )}
      </IonContent>

      {loaded && (
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton className="fab-main" size="small">
            <IonIcon icon={menuSharp} />
          </IonFabButton>
          <IonFabList side="top">
            {getRole?.role_menu_edit === 1 && (
              <IonFabButton
                data-desc={`${t("edit")}`}
                color={"warning"}
                onClick={() => presentForm({ cssClass: "modal-fullscreen" })}
              >
                <IonIcon icon={createOutline} />
              </IonFabButton>
            )}
            {getRole?.role_menu_delete === 1 && (
              <IonFabButton
                color="danger"
                data-desc={`${t("delete")}`}
                onClick={handleDelete}
              >
                <IonIcon icon={trashBinOutline} />
              </IonFabButton>
            )}
            <IonFabButton
              data-desc={`History ${t("module.visit")}`}
              onClick={() => {
                presentHistoryVisit({
                  cssClass: "modal-fullscreen",
                });
              }}
            >
              <IonIcon icon={mapOutline} />
            </IonFabButton>
            <IonFabButton
              data-desc={`History ${t("module.customer-follow-up")}`}
              onClick={() => {
                presentHistoryCustomerFollowUp({
                  cssClass: "modal-fullscreen",
                });
              }}
            >
              <IonIcon icon={pushOutline} />
            </IonFabButton>
          </IonFabList>
        </IonFab>
      )}
    </IonPage>
  );
};

export default Detail;
